import "./Ages.scss";

import CircularProgress from "@mui/material/CircularProgress";
import { Typography } from "../../ui";
import { CardWhite, GeographyData, RechartsPie } from "..";
import { useEffect, useState } from "react";
import { DOMAIN_API_HOST } from "../../configs/main";
import {
  MOCK_STATS_DATA,
  delayMockStatsLoading,
  getMockStatsMonth,
  isSpecialStatsClient,
} from "../../modules/Statistics/specialClientMockStats";

function Ages({ counter_id, date_from, date_to, selected_campaigns = [], user_id, data: externalData }) {
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState(externalData ?? []);
  const geoMap = new Map();

  useEffect(() => {
    if (externalData !== undefined) {
      setData(externalData ?? []);
      setLoading(false);
    }
  }, [externalData]);

  useEffect(() => {
    if (externalData !== undefined) return;

    let isCancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setData([]);

        if (isSpecialStatsClient(user_id)) {
          await delayMockStatsLoading();
          if (isCancelled) return;

          const monthKey = getMockStatsMonth(date_from, date_to);
          setData(monthKey ? MOCK_STATS_DATA[monthKey].ages : []);
          return;
        }
        
        const campaigns = (selected_campaigns || []).filter(
          (id) => id && id !== "all_campaigns" && id !== "0"
        );

        let baseUrl = `${DOMAIN_API_HOST}/api/v1/ya-metrika/stat/ages?counter_id=${counter_id}&date_from=${date_from?.toLocaleDateString(
          "en-CA"
        )}&date_to=${date_to?.toLocaleDateString("en-CA")}`;

        let url = baseUrl;
        if (campaigns.length > 0) {
          campaigns.forEach((id) => {
            url = url.concat(`&campaign_ids=${id}`);
          });
        }

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });

        if (!response.ok) {
          if (campaigns.length > 0) {
            const fallbackResponse = await fetch(baseUrl, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            if (fallbackResponse.ok) {
              const fallbackData = await fallbackResponse.json();
              setData(fallbackData);
            } else {
              setData([]);
            }
          } else {
            setData([]);
          }
          return;
        }

        const data = await response.json();
        const hasData = data.length > 0 && data.some(item => item.value > 0);

        if (!hasData && campaigns.length > 0) {
          const fallbackResponse = await fetch(baseUrl, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          });
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            setData(fallbackData);
          } else {
            setData(data);
          }
        } else {
          setData(data);
        }

      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    if (counter_id) {
      fetchData();
    }

    return () => {
      isCancelled = true;
    };
  }, [counter_id, date_from, date_to, selected_campaigns, user_id, externalData]);

  if (!counter_id) {
    return (
      <div className="Geography">
        <CardWhite>
          <Typography variant="h2" color="dark-gray">
            Возраст
          </Typography>
          <center style={{ marginTop: "60%" }}>Выберите счётчик</center>
        </CardWhite>
      </div>
    );
  }

  for (const item of data) {
    const regionName = item.name;
    const regionValue = item.value;

    geoMap.set(regionName, (geoMap.get(regionName) || 0) + regionValue);
  }

  let geoItems = Array.from(geoMap.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  const total = geoItems.reduce((sum, item) => sum + item.value, 0);

  geoItems = geoItems.map((item, index, array) => {
    const percent = total > 0 ? (item.value / total) * 100 : 0;
    if (index === array.length - 1) {
      const sumPrev = array.slice(0, -1).reduce((s, i) => s + (i.value / total) * 100, 0);
      return {
        id: index,
        name: item.name,
        value: Number((100 - sumPrev).toFixed(2))
      };
    }
    return {
      id: index,
      name: item.name,
      value: Number(percent.toFixed(2))
    };
  });

  const colors = ["#f9a93f", "#a864f8", "#3f73f9", "#3ff97a", "#ffed42", "gray"];

  const allZeros = data.length > 0 && data.every(item => item.value === 0);

  return (
    <div className="Geography">
      <CardWhite>
        <Typography variant="h2" color="dark-gray">
          Возраст
        </Typography>
        {isLoading ? (
          <div className="widget-loader">
            <CircularProgress size={22} sx={{ color: "#3f73f9" }} />
            <span>Загрузка данных...</span>
          </div>
        ) : allZeros || geoItems.length === 0 ? (
          <center style={{ marginTop: "60%" }}>Данные не найдены</center>
        ) : (
          <>
            <div className="graph">
              <RechartsPie geoItems={geoItems} colors={colors} />
            </div>

            <div style={{ marginTop: "20px" }}>
              {geoItems.map((geoItem, index) => (
                <GeographyData
                  name={geoItem.name}
                  value={geoItem.value}
                  color={colors[index % colors.length]}
                  key={geoItem.id}
                />
              ))}
            </div>
          </>
        )}
      </CardWhite>
    </div>
  );
}

export default Ages;